import React, { useState, useEffect, useCallback } from "react";
import { get, post } from "../../api";

// Super-admin surface to re-authenticate the headless Claude operator after its
// subscription token expires (the worker emails a signed link here). Works two
// ways: opened from the email link (?link=…, no login needed) OR from the admin
// menu while logged in as super-admin. Companion to prigmar-worker
// services/claudeAuth.js + back/routes/claudeAuth.js.
export default function ClaudeAuth() {
  const link = (() => {
    try { return new URLSearchParams(window.location.search).get("link") || ""; }
    catch (_) { return ""; }
  })();

  const [status, setStatus] = useState(null);     // { authNeeded, neededAt, reason, hasToken }
  const [linkValid, setLinkValid] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);           // { type, text }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // Status is super-admin only; ignore failures (emailed user may not be logged in).
    try { setStatus(await get("/api/admin/claude-auth/status")); } catch (_) { setStatus(null); }
    if (link) {
      try {
        const r = await get(`/api/admin/claude-auth/link-valid?token=${encodeURIComponent(link)}`);
        setLinkValid(!!(r && r.valid));
      } catch (_) { setLinkValid(false); }
    }
    setLoading(false);
  }, [link]);
  useEffect(() => { load(); }, [load]);

  const canSubmit = !!status || linkValid; // logged-in super-admin OR a valid link

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await post("/api/admin/claude-auth/token", { token: token.trim(), link });
      setMsg({ type: "ok", text: (r && r.message) || "Token saved. The AI operator will resume shortly." });
      setToken("");
      load();
    } catch (err) {
      setMsg({ type: "err", text: (err && (err.error || err.message)) || "Could not save the token." });
    }
    setBusy(false);
  }

  return (
    <div className="container" style={{ maxWidth: 720, margin: "0 auto", padding: "2rem 1rem" }}>
      <h2><i className="fa fa-key" style={{ marginRight: 8 }} />Claude authentication</h2>
      <p style={{ color: "#5a6b78" }}>
        The AI operator (email classification, content generation, self-construction) authenticates
        with Claude using a subscription token. When it expires those jobs <b>pause</b> — they are
        held, not lost — until a fresh token is submitted here.
      </p>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {status && status.authNeeded && (
            <div style={banner("#fdecea", "#b3261e")}>
              <b>Re-authentication needed.</b>{" "}
              Claude auth has been failing{status.neededAt ? ` since ${new Date(status.neededAt).toLocaleString()}` : ""}.
              {status.reason ? <div style={{ marginTop: 6, fontSize: ".88rem", opacity: .85 }}>Reason: {status.reason}</div> : null}
            </div>
          )}
          {status && !status.authNeeded && (
            <div style={banner("#e7f7ef", "#0a7d52")}>
              <b>Operator authentication is healthy.</b>{status.hasToken ? " A long-lived token is stored." : ""}
              {status.tokenUpdatedAt ? ` (token set ${new Date(status.tokenUpdatedAt).toLocaleString()})` : ""}
            </div>
          )}
          {!status && link && !linkValid && (
            <div style={banner("#fdecea", "#b3261e")}>This link has expired or is invalid. Open <code>/admin/claude-auth</code> while logged in as super-admin, or request a new link.</div>
          )}
          {!status && link && linkValid && (
            <div style={banner("#eef4ff", "#0f4c75")}>You opened a valid re-authentication link. Paste a fresh token below and submit.</div>
          )}

          <div style={{ background: "#f6f8fa", border: "1px solid #e1e7ec", borderRadius: 10, padding: "1rem 1.2rem", margin: "1.2rem 0" }}>
            <b>How to get a token</b>
            <ol style={{ margin: ".5rem 0 0", paddingLeft: "1.2rem", lineHeight: 1.7 }}>
              <li>On a machine logged into the Claude subscription, run: <code>claude setup-token</code></li>
              <li>Copy the token it prints (starts with <code>sk-ant-oat…</code>).</li>
              <li>Paste it below and submit.</li>
            </ol>
          </div>

          <form onSubmit={submit}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Claude setup token</label>
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="sk-ant-oat01-…"
              rows={3}
              style={{ width: "100%", fontFamily: "ui-monospace, monospace", padding: ".7rem .8rem", border: "1px solid #c9d6e0", borderRadius: 8 }}
            />
            <div style={{ marginTop: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={busy || !token.trim() || !canSubmit}>
                {busy ? "Saving…" : "Save token & resume operator"}
              </button>
              {!canSubmit && !loading && (
                <span style={{ marginLeft: 12, color: "#b3261e", fontSize: ".88rem" }}>
                  Log in as super-admin or open this page from the alert-email link to submit.
                </span>
              )}
            </div>
            {msg && (
              <p style={{ marginTop: 12, color: msg.type === "ok" ? "#0a7d52" : "#b3261e", fontWeight: 600 }}>{msg.text}</p>
            )}
          </form>
        </>
      )}
    </div>
  );
}

function banner(bg, fg) {
  return { background: bg, color: fg, border: `1px solid ${fg}33`, borderRadius: 8, padding: ".8rem 1rem", margin: "1rem 0" };
}
